import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BlingProvider } from "@/lib/blingContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CancelSubscription from "@/pages/CancelSubscription";
// Dashboard route removed (deprecated) — /app now redirects to /bling-discount.
import Orders from "@/pages/Orders";
import Billing from "@/pages/Billing";
import MyBilling from "@/pages/MyBilling";
import MyAccount from "@/pages/MyAccount";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import Integrations from "@/pages/Integrations";
import PlatformSettings from "@/pages/PlatformSettings";
import ZplConverter from "@/pages/ZplConverter";
import EanGenerator from "@/pages/EanGenerator";
import BlingInstructions from "@/pages/BlingInstructions";
import DiscountRules from "@/pages/DiscountRules";
import Printing from "@/pages/Printing";
import Expedition from "@/pages/Expedition";
import AuditLogs from "@/pages/AuditLogs";
import BlingDiscount from "@/pages/BlingDiscount";
import Catalog from "@/pages/Catalog";
import Pricing from "@/pages/Pricing";
import AdminDashboard from "@/pages/AdminDashboard";
import Support from "@/pages/Support";
import AdminSupport from "@/pages/AdminSupport";
import AdminAnnouncements from "@/pages/AdminAnnouncements";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function ProtectedRaw({ children }) {
  // Same auth guard as Protected but WITHOUT the sidebar Layout.
  // Used for full-page landings like the cancel-subscription flow
  // that come from a transactional e-mail link.
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) {
    // Preserve the current URL (including query string) so the login
    // page can bounce the user back after re-auth.
    const next = window.location.pathname + window.location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return children;
}

function PlatformAdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BlingProvider>
        <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Public — Bling homologation documentation (no auth required
              so Bling reviewers can access the URL directly). */}
          <Route path="/instrucoes-bling" element={<BlingInstructions />} />

          {/* Authenticated app */}
          <Route path="/app" element={<Protected><Navigate to="/bling-discount" replace /></Protected>} />
          <Route path="/orders" element={<Protected><Orders /></Protected>} />
          <Route path="/billing" element={<Protected><Billing /></Protected>} />
          <Route path="/minha-conta" element={<Protected><MyAccount /></Protected>} />
          <Route path="/cancel-subscription" element={<ProtectedRaw><CancelSubscription /></ProtectedRaw>} />
          <Route path="/companies" element={<Protected><Companies /></Protected>} />
          <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
          <Route path="/zpl-converter" element={<Protected><ZplConverter /></Protected>} />
          <Route path="/ean-generator" element={<Protected><EanGenerator /></Protected>} />
          <Route path="/discount-rules" element={<Protected><DiscountRules /></Protected>} />
          <Route path="/printing" element={<Protected><Printing /></Protected>} />
          <Route path="/expedition" element={<Protected><Expedition /></Protected>} />
          <Route path="/bling-discount" element={<Protected><BlingDiscount /></Protected>} />
          <Route path="/catalogo" element={<Protected><Catalog /></Protected>} />
          <Route path="/precificacao" element={<Protected><Pricing /></Protected>} />
          <Route path="/support" element={<Protected><Support /></Protected>} />

          {/* Platform admin only */}
          <Route path="/admin" element={<PlatformAdminOnly><AdminDashboard /></PlatformAdminOnly>} />
          <Route path="/admin/support" element={<PlatformAdminOnly><AdminSupport /></PlatformAdminOnly>} />
          <Route path="/admin/announcements" element={<PlatformAdminOnly><AdminAnnouncements /></PlatformAdminOnly>} />
          <Route path="/users" element={<PlatformAdminOnly><Users /></PlatformAdminOnly>} />
          <Route path="/platform-settings" element={<PlatformAdminOnly><PlatformSettings /></PlatformAdminOnly>} />
          <Route path="/audit-logs" element={<PlatformAdminOnly><AuditLogs /></PlatformAdminOnly>} />

          {/* Fallback: authenticated goes to app, guest to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </BlingProvider>
    </AuthProvider>
  );
}
